export function parseAmountFromQueryParam(searchParams: URLSearchParams): number {
  const amount = parseFloat(searchParams.get('amount') ?? '0')
  return Number.isFinite(amount) && amount > 0 ? amount : 0
}

export function buildAmountQueryParam(amount: number): string {
  return amount > 0 ? `?amount=${amount}` : ''
}

export enum AmountAdjustmentReason {
  NONE = 'NONE',
  MIN = 'MIN',
  MAX = 'MAX',
  STEPPER = 'STEPPER'
}

/** Lower of fund max and the highest payment-method cap. Same max the payment page validates against. */
export function getEffectiveMaxAmount(
  fundMaxAmount: number,
  paymentMethods?: { maximumAllowedAmount?: number }[] | null
): number {
  const fundMax = fundMaxAmount ?? 0
  if (!paymentMethods?.length) return fundMax

  const maxPaymentMethodAmount = paymentMethods.reduce((currentMax, method) => {
    return Math.max(currentMax, method?.maximumAllowedAmount ?? 0)
  }, 0)

  if (maxPaymentMethodAmount <= 0) return fundMax
  if (fundMax <= 0) return maxPaymentMethodAmount
  return maxPaymentMethodAmount < fundMax ? maxPaymentMethodAmount : fundMax
}

/** Positive integer rupees. Missing / 0 / fractional → ₹1. Same rule as keypad + normalize. */
export function getEffectiveStepper(stepper?: number | null): number {
  if (typeof stepper !== 'number' || !Number.isFinite(stepper) || stepper < 1) return 1
  return Math.round(stepper)
}

/**
 * Advisory seed: clamp to min/max, then nearest stepper (₹1 if missing). Result is always an integer.
 * `adjustment` drives the payment-page toast (MIN / MAX / STEPPER).
 */
export function normalizeAllocationAmount(
  rawAmount: number,
  rules: { minAmount: number; maxAmount: number; stepper?: number }
): { amount: number; adjustment: AmountAdjustmentReason } {
  const minAmount = rules?.minAmount ?? 0
  const maxAmount = rules?.maxAmount ?? 0
  const stepper = getEffectiveStepper(rules?.stepper)
  let amount = rawAmount
  let adjustment = AmountAdjustmentReason.NONE

  // Bound first so toast reason stays MIN/MAX when the seed is outside the band.
  if (amount < minAmount) {
    amount = minAmount
    adjustment = AmountAdjustmentReason.MIN
  } else if (maxAmount > 0 && amount > maxAmount) {
    amount = maxAmount
    adjustment = AmountAdjustmentReason.MAX
  }

  // Nearest multiple; epsilon ignores binary float noise (1000.0000000002).
  const snappedAmount = Math.round(amount / stepper) * stepper
  if (Math.abs(snappedAmount - amount) > 1e-10) {
    amount = snappedAmount
    if (maxAmount > 0 && amount > maxAmount) {
      // Snap went over max → last stepper tick that still fits.
      amount = Math.floor(maxAmount / stepper) * stepper
      if (adjustment === AmountAdjustmentReason.NONE) {
        adjustment = AmountAdjustmentReason.MAX
      }
    } else if (amount < minAmount) {
      // Snap went under min → first stepper tick at or above min.
      amount = Math.ceil(minAmount / stepper) * stepper
      if (adjustment === AmountAdjustmentReason.NONE) {
        adjustment = AmountAdjustmentReason.MIN
      }
    } else if (adjustment === AmountAdjustmentReason.NONE) {
      adjustment = AmountAdjustmentReason.STEPPER
    }
  }

  // Snap-up to clear min can overshoot max when no tick sits in range.
  if (maxAmount > 0 && amount > maxAmount) {
    amount = Math.floor(maxAmount / stepper) * stepper
  }
  if (amount < minAmount) {
    amount = minAmount
  }

  // Integerize. Math.round alone can drop below a decimal min (1000.4 → 1000), so re-clamp.
  amount = Math.round(amount)
  if (minAmount > 0 && amount < minAmount) {
    amount = Math.ceil(minAmount)
  }
  if (maxAmount > 0 && amount > maxAmount) {
    amount = Math.floor(maxAmount)
  }

  return { amount, adjustment }
}
